import type { components } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';
import {
  DEFAULT_CHECKOUT_RETURN_PATH,
  type CheckoutReturnPath
} from '@lib/checkout/return-paths';

// FFH-1849: `firstLandingPage` is added to the backend CheckoutBody schema in
// this same change. Until the OpenAPI types are regenerated against the merged
// backend (`npm run generate:types`), extend the generated type locally so the
// field type-checks; the intersection is a no-op once the types are regenerated.
// FFH-1742: `trialPeriodDays` rides the same pattern for the trial arm of the
// acquisition test.
type CheckoutBody = components['schemas']['CheckoutBody'] & {
  firstLandingPage?: string;
  trialPeriodDays?: number;
};
type UtmFirstTouch = NonNullable<CheckoutBody['utmFirstTouch']>;
type UtmLastTouch = NonNullable<CheckoutBody['utmLastTouch']>;

export type EmbeddedCheckoutParams = {
  accessToken: string;
  planId: number;
  email?: string;
  externalId?: string;
  coupon?: string;
  referral?: string | null;
  fbClickId?: string | null;
  fbpCookie?: string | null;
  purchasePixelEventId?: string;
  /**
   * FFH-1160 attribution payload. UTMs use BARE keys (`source`,
   * `medium`, …); the cookie writer keeps `utm_*` prefixed keys, so
   * callers must remap via `toBareUtm` before passing.
   */
  utmFirstTouch?: UtmFirstTouch;
  utmLastTouch?: UtmLastTouch;
  sourceUrl?: NonNullable<CheckoutBody['sourceUrl']>;
  // FFH-1849: first page the buyer landed on (pathname), read from the
  // write-once `first_landing_page` cookie at checkout.
  firstLandingPage?: string;
  consentState?: NonNullable<CheckoutBody['consentState']>;
  /**
   * FFH-970: structured 3-axis consent (preferred). The backend treats this as
   * authoritative over the legacy single-string `consentState` and forwards it
   * to each platform's processing-options field on the Purchase CAPI.
   */
  consent?: NonNullable<CheckoutBody['consent']>;
  /**
   * FFH-1533: allowlisted page the user finally lands on after /thank-you.
   * Stripe always returns to /thank-you; a non-default value rides along as a
   * query param and steers where /thank-you forwards them (the funnel paywall
   * passes /funnel-welcome).
   *
   * /thank-you used to force a logout here, purely to re-run the Auth0
   * post-login Action and mint the subscription claim. FFH-1597 replaced that
   * with refreshUntilSubscriptionClaim() — bounded refresh-token grants that
   * keep the session alive — so the user arrives at returnPath authenticated.
   * Note refresh grants deliberately do NOT stamp app_metadata.last_login_at,
   * so this path fires no login_completed event (see FFH-1986).
   */
  returnPath?: CheckoutReturnPath;
  /**
   * FFH-1742: number of free trial days the trial arm's paywall requests.
   * The API is authoritative: it clamps the range and Ultra plans never get
   * a trial. Omitted or 0 keeps the standard upfront checkout.
   */
  trialPeriodDays?: number;
};

type EmbeddedCheckoutResult =
  | { clientSecret: string }
  // FFH-2592: the HTTP status and the API's error code ride out alongside the
  // error so the checkout page can tell a 409 HAS_LIVE_SUBSCRIPTION (route
  // them to change-plan) from a genuine failure (show the error screen).
  // Mirrors the `failureStatus` confirmCheckout already returns. Null when the
  // request never completed, so there is no response to read from.
  | { error: true; failureStatus: number | null; failureCode: string | null };

const definedOnly = <Value>(value: Value | null | undefined | '') =>
  value === null || value === undefined || value === '' ? undefined : value;

export const createEmbeddedCheckoutSession = async ({
  accessToken,
  planId,
  email,
  externalId,
  coupon,
  referral,
  fbClickId,
  fbpCookie,
  purchasePixelEventId,
  utmFirstTouch,
  utmLastTouch,
  sourceUrl,
  firstLandingPage,
  consentState,
  consent,
  returnPath,
  trialPeriodDays
}: EmbeddedCheckoutParams): Promise<EmbeddedCheckoutResult> => {
  // Stripe always returns to /thank-you (the analytics + claim-refresh
  // interstitial); a non-default returnPath rides along in the query so
  // /thank-you knows where to forward the user once the refresh settles. The
  // backend appends plan_id/session via the URL API, so the extra param is safe.
  const returnUrl =
    returnPath && returnPath !== DEFAULT_CHECKOUT_RETURN_PATH
      ? `${process.env.NEXT_PUBLIC_HOSTNAME}${DEFAULT_CHECKOUT_RETURN_PATH}?returnPath=${returnPath}`
      : `${process.env.NEXT_PUBLIC_HOSTNAME}${DEFAULT_CHECKOUT_RETURN_PATH}`;

  const body: CheckoutBody = {
    planId,
    uiMode: 'embedded',
    returnUrl,
    email: definedOnly(email),
    externalId: definedOnly(externalId),
    coupon: definedOnly(coupon),
    referral: definedOnly(referral),
    fbClickId: definedOnly(fbClickId),
    fbpCookie: definedOnly(fbpCookie),
    purchasePixelEventId: definedOnly(purchasePixelEventId),
    ...(utmFirstTouch && { utmFirstTouch }),
    ...(utmLastTouch && { utmLastTouch }),
    ...(definedOnly(sourceUrl) && { sourceUrl }),
    ...(definedOnly(firstLandingPage) && { firstLandingPage }),
    ...(definedOnly(consentState) && { consentState }),
    ...(consent && { consent }),
    // A trial of 0 days is meaningless to request; omit it so the body stays
    // byte-identical for every non-trial checkout.
    ...(trialPeriodDays && trialPeriodDays > 0 && { trialPeriodDays })
  };

  // FFH-1160: `credentials: 'include'` sends the first-party attribution
  // cookies so the backend can read them off the request. The HttpOnly
  // click-ID cookies the Cloudflare worker writes (fbclid / gclid / ttclid /
  // gbraid / wbraid / sc_click_id) can ONLY reach the server this way;
  // `_fbc` / `_fbp` ride along too but are non-HttpOnly — the FB pixel SDK
  // writes them and `lib/fpixel.ts` reads them in JS. `/billing/checkout` is
  // on the credentialed CORS allowlist (apps/api/src/app.ts).
  const { data, error, response } = await ffhClient().POST(
    '/billing/checkout',
    {
      headers: authHeader(accessToken),
      body,
      credentials: 'include'
    }
  );

  if (error || !data?.data?.clientSecret) {
    return {
      error: true,
      failureStatus: response?.status ?? null,
      failureCode: error?.error?.code ?? null
    };
  }

  return { clientSecret: data.data.clientSecret };
};

type UpdateSubscriptionResult = {
  hasChangedPlan: boolean;
  error?: string;
};

export const updateStripeSubscriptionV2 = async ({
  payload,
  accessToken
}: {
  payload: { tier: string; term: string; coupon?: string };
  accessToken: string;
}): Promise<UpdateSubscriptionResult> => {
  const { data, error } = await ffhClient().PUT('/billing/checkout', {
    headers: authHeader(accessToken),
    body: {
      tier: payload.tier,
      term: payload.term,
      coupon: definedOnly(payload.coupon)
    }
  });

  if (error || !data?.data?.hasChangedPlan) {
    return {
      hasChangedPlan: false,
      error: error?.error?.message ?? 'Failed to update subscription'
    };
  }

  return { hasChangedPlan: true };
};

export const confirmCheckout = async ({
  accessToken,
  sessionId
}: {
  accessToken: string;
  sessionId: string;
}): Promise<{
  ok: boolean;
  // HTTP status of the confirm call on failure, so the caller's diagnostics
  // can tell a 401 (token race) from a 5xx (backend down). Null when the
  // request never completed.
  failureStatus: number | null;
  hashedEmail: string | null;
  purchaseEventId: string | null;
  isTrial: boolean;
  amount: number | null;
  currency: string | null;
}> => {
  const { data, error, response } = await ffhClient().POST('/billing/confirm', {
    headers: authHeader(accessToken),
    body: { sessionId }
  });

  if (error || !data?.data) {
    // `ok: false` marks the confirm as failed (the generated client swallows
    // HTTP errors into this branch). The confirmation page must not guess
    // trial-vs-upfront from the defaults below — it pushes nothing on !ok.
    return {
      ok: false,
      failureStatus: response?.status ?? null,
      hashedEmail: null,
      purchaseEventId: null,
      isTrial: false,
      amount: null,
      currency: null
    };
  }

  // FFH-359: `purchaseEventId` was added to the backend ConfirmResponse so the
  // thank-you page uses the server-authoritative pixel↔CAPI dedup id instead of
  // localStorage. `isTrial` drives the start_trial-vs-purchase branch on the
  // confirmation page. The generated OpenAPI types lag until regenerated, so
  // read both defensively; a backend that doesn't send isTrial yet reads as
  // false (the upfront-purchase path, matching prior behaviour).
  const confirmData = data.data as {
    hashedEmail: string | null;
    purchaseEventId?: string | null;
    isTrial?: boolean;
    // FFH-1847: actual charged amount + currency for the localised purchase
    // push. Optional until the OpenAPI types regenerate; read defensively.
    amount?: number | null;
    currency?: string | null;
  };

  return {
    ok: true,
    failureStatus: null,
    hashedEmail: confirmData.hashedEmail,
    purchaseEventId: confirmData.purchaseEventId ?? null,
    isTrial: confirmData.isTrial === true,
    amount: confirmData.amount ?? null,
    currency: confirmData.currency ?? null
  };
};

type ProrationResult = Record<string, unknown> & { error?: string };

export const getProrationV2 = async ({
  accessToken,
  term,
  tier,
  coupon
}: {
  accessToken: string;
  term: string;
  tier: string;
  coupon?: string;
}): Promise<ProrationResult> => {
  const { data, error } = await ffhClient().POST('/billing/proration', {
    headers: authHeader(accessToken),
    body: { tier, term, coupon: definedOnly(coupon) }
  });

  if (error || !data?.data) {
    return { error: error?.error?.message ?? 'Failed to preview proration' };
  }

  return data.data;
};
